import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Button,
  Stack,
  CircularProgress,
  SwipeableDrawer,
  Grid,
  Tooltip,
  useMediaQuery,
  useTheme,
  Drawer,
} from "@mui/material";
import { LocationOn } from "@mui/icons-material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { TravelIcon } from "../../assets/Icons/TravelIcon";
import { PieChart as PieChartIcon } from "lucide-react";
import CandidateDetail from "../../components/Playground/CandidateDetail/CandidateDetail";
import ScoreCardDetail from "./ScoreCardDetail";
import { getMockScoreCard, getMockCandidateData } from "../../api/agent";
import { useMutation } from "@tanstack/react-query";
import { usePlaygroundContext } from "../Playground";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MessagePopup from "../../layout/messagePopup";
import Link from "@mui/material/Link";
import CloseIcon from "@mui/icons-material/Close";
import CandidateData from "./CandidateMatchData";

interface WorkAuthorization {
  citizen: boolean;
  permanentResident: boolean;
  other: boolean;
}

interface CandidateSearchResults {
  annualRate: number | null;
  difference_radius: any;
  email: string;
  eximiusId: string;
  firstName: string;
  hotListed: string;
  hourlyRate: number;
  id: string;
  jobTitle: string;
  lastName: string;
  lastUpdated: string;
  location: {
    city: string;
    country: string;
    countryCode: string;
    latitude: number;
    longitude: number;
    postalCode: string | null;
    state: string;
    street: string;
  };
  phone: string[];
  source: string;
  status: string;
  willingToRelocate: boolean;
  yearsOfExperience: number;
  scorecard: string;
}

export interface CandidateData {
  eximScore: number;
  search_results: CandidateSearchResults;
  annualRate: number | null;
  difference_radius: any;
  email: string;
  eximiusId: string;
  firstName: string;
  hotListed: string;
  hourlyRate: number;
  id: string;
  jobTitle: string;
  lastName: string;
  lastUpdated: string;
  location: {
    city: string;
    country: string;
    countryCode: string;
    latitude: number;
    longitude: number;
    postalCode: string | null;
    state: string;
    street: string;
  };
  phone: string[];
  source: string;
  status: string;
  willingToRelocate: boolean;
  yearsOfExperience: number;
  city: string;
  state: string;
  education: string[];
  certifications: string[];
  experience: number;
  workAuthorization: WorkAuthorization;
  jobDescription: any;
  generateJd: any;
  scorecard: string;
}

interface MatchCardProps {
  loading: boolean;
  searchScoreCard: any;
  name: string;
  experience: number;
  location: any;
  distance: number;
  match: number;
  showHomeIcon: boolean;
  showTravelIcon: boolean;
  handleDownload: () => void;
  handleScorecardDownload: () => void;
  onClick: () => void;
  handleNameClick: any;
  candidate: any;
  sx?: any;
  setCandidateDetailsOpen: any;
  setLoadingCandidate: any;
}

const MatchCard: React.FC<MatchCardProps> = ({
  loading,
  searchScoreCard,
  name,
  experience,
  location,
  distance,
  match,
  showHomeIcon,
  showTravelIcon,
  handleDownload,
  handleScorecardDownload,
  onClick,
  handleNameClick,
  candidate,
  sx,
  setCandidateDetailsOpen,
  setLoadingCandidate,
}: any) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const playgroundContext = usePlaygroundContext();
  if (!playgroundContext) {
    throw new Error("PlaygroundContext is not available");
  }
  const { playground, setPlayground } = playgroundContext;
  const getDegrees = (candidate: any) => {
    if (candidate?.generateJd?.education) {
      // Map through the education array and extract the degree
      return candidate.generateJd.education.map(
        (edu: { degree: any }) => edu.degree
      );
    }
    return []; // Return an empty array if there's no education data
  };
  const MainComponent: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);

    const toggleDrawer = (open: boolean) => () => {
      setIsOpen(open);
    };

    return (
      <>
        <Typography sx={{ color: "white", fontSize: "16px" }}>
          Lorem ipsum dolor sit amet consectetur adipisicing elit. Sapiente,
          ratione ex obcaecati voluptate odio totam non? Eum magni enim,
          possimus non repellendus officia ipsum explicabo itaque totam, hic,
          nemo similique.
          <Link
            component="button"
            onClick={toggleDrawer(true)}
            sx={{
              color: "#66B2FF",
              textDecoration: "none",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            Here’s why
          </Link>
        </Typography>

        {/* Right Panel */}
        <Drawer anchor="right" open={isOpen} onClose={toggleDrawer(false)}>
          <Box sx={{ width: 350, p: 2, position: "relative" }}>
            {/* Close Button (X) */}
            <IconButton
              onClick={toggleDrawer(false)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "#333",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Drawer>
      </>
    );
  };

  const generatedResponse = playground.jobDescription;

  const handleScoreCardClick = () => {
    let data = generatedResponse.messageList?.[0];
    let certifcations = getCertifications(data?.certifications);
    let education = getEducation(data?.education);
    let skills = getSkills(data?.functionalSkills, data?.technicalSkills);

    const payload = {
      jobDescription: candidate?.JobDescription,
      firstname: candidate?.firstName || "",
      lastname: candidate?.lastName || "",
      email: candidate?.email || "",
      eximiusId: candidate?.eximiusId,
      eximScore: candidate?.eximScore || "N/A",
      jobTitle: candidate?.jobTitle || "",
      phone: candidate?.phone || "",
      skills: skills,
      education: education,
      certificationAndLicenses: certifcations,
      industry: data?.industry?.name ? [data.industry.name] : [],
      domain: data?.domain?.name ? [data.domain.name] : [],
    };
    searchScoreCard(payload);
  };

  const getCertifications = (
    certifications: { name?: string; requirement: string }[]
  ): string[] => {
    return certifications.filter((cert) => cert.name).map((cert) => cert.name!);
  };

  const getEducation = (
    education: { degree?: string; requirement: string }[]
  ): string[] => {
    return education.filter((edu) => edu.degree).map((edu) => edu.degree!);
  };

  type Skill = { skill?: string };

  const getSkills = (
    functionalSkills: Skill[],
    technicalSkills: Skill[]
  ): string[] => {
    const skills1 = functionalSkills
      .filter((skill) => skill.skill)
      .map((skill) => skill.skill!);

    const skills2 = technicalSkills
      .filter((skill) => skill.skill)
      .map((skill) => skill.skill!);

    return [...skills1, ...skills2];
  };

  const handleCandidateNameClick = () => {
    setCandidateDetailsOpen(true);
    setLoadingCandidate(true);
    const payload = {
      jobDescription: candidate?.JobDescription,
      firstname: candidate?.firstName || "",
      lastname: candidate?.lastName || "",
      email: candidate?.email || "",
      eximiusId: candidate?.eximiusId,
      eximScore: candidate?.eximScore || "N/A",
      job_title: candidate?.jobTitle || "",
      phone: candidate?.phone || "",
    };

    handleNameClick(payload);
  };

  const formatDistance = (distance: number | undefined | null) => {
    if (distance === undefined || distance === null || distance === 0) {
      return "Local";
    }
    if (distance > 100) {
      return "> 100 miles";
    }
    return `${distance} miles`;
  };

  return (
    <Box
      onClick={onClick}
      display="flex"
      alignItems="center"
      color="#FFFFFF"
      p={0}
      ml={0}
      borderRadius={3}
      boxShadow={2}
      width={isMobile ? "95%" : isTablet ? "80%" : "60%"}
      mb={2}
      sx={{
        ...sx,
        marginLeft: "auto",
        marginRight: "auto",
      }}
    >
      <Grid
        container
        display="flex"
        justifyContent={"flex-end"}
        alignItems="center"
        spacing={1}
        m={0}
      >
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            minWidth: isMobile ? "55px" : "75px",
            minHeight: isMobile ? "55px" : "75px",
            paddingRight: isMobile ? "15px" : " ",
          }}
        >
          <CircularProgress
            variant="determinate"
            value={100}
            size={isMobile ? 55 : 75}
            thickness={4}
            sx={{ color: "#D9D9D9" }}
          />

          <CircularProgress
            variant="determinate"
            value={match}
            size={isMobile ? 55 : 75}
            thickness={4}
            sx={{ color: "#091750", position: "absolute", top: 0, left: 0 }}
          />

          <Box
            position="absolute"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: isMobile ? "12px" : "14px",
                fontWeight: "bold",
                color: "#FFFFFF",
              }}
            >
              {match}%
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: isMobile ? "8px" : "10px",
                fontWeight: "bold",
                color: "#FFFFFF",
              }}
            >
              Match
            </Typography>
          </Box>
        </Box>

        {/* User Details */}
        <Box
          ml={isMobile ? 0 : 2}
          flexGrow={1}
          display="flow"
          alignItems="center"
          gap={isMobile ? 0 : 2}
          sx={{ maxWidth: isMobile ? "55%" : "unset" }}
        >
          <Box display="flex" alignItems="center">
            <Typography
              fontWeight="bold"
              sx={{
                cursor: "pointer",
                color: "#FFFFFF",
                fontSize: isMobile ? "12px" : "inherit",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name || "N/A"}
            </Typography>
          </Box>
          {experience > 0 && (
            <Box display="flex" alignItems="center">
              <DescriptionOutlinedIcon
                sx={{
                  fontSize: { xs: "11px", sm: "11px", md: "14px" },
                  mr: 0.5,
                  color: "#FFFFFF",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: "#FFFFFF",
                  fontSize: isMobile ? "11px" : "inherit",
                }}
              >
                {experience} Yrs
              </Typography>
            </Box>
          )}

          {(location?.city || location?.state) && (
            <Box display="flex" alignItems="center">
              <LocationOn
                fontSize="small"
                sx={{
                  mr: 0.5,
                  color: "#FFFFFF",
                  width: isMobile ? "14px" : "17px",
                  marginLeft: "-1px",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: "#FFFFFF",
                  fontSize: isMobile ? "11px" : "inherit",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: isMobile ? "100px" : "unset",
                }}
              >
                {location?.city && location?.state
                  ? `${location.city}, ${location.state}`
                  : location?.city
                  ? location.city
                  : ""}
              </Typography>
            </Box>
          )}

          <Box display="flex" alignItems="center">
            {(location?.city || location?.state) && (
              <Box display="flex" alignItems="center">
                {distance === 0 &&
                  showHomeIcon &&
                  location.city &&
                  location.state && (
                    <Tooltip title="Local">
                      <HomeOutlinedIcon
                        sx={{
                          marginRight: 0.5,
                          marginTop: "3px",
                          fontSize: isMobile ? "12px" : "14px",
                          color: "#FFFFFF",
                        }}
                      />
                    </Tooltip>
                  )}
                <Tooltip title={`Distance: ${distance} miles`}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#FFFFFF",
                      fontSize: isMobile ? "11px" : "inherit",
                    }}
                  >
                    {formatDistance(distance)}
                  </Typography>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>

        {/* Right Section for Scorecard and View Profile */}
        <Grid
          item
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          alignItems="flex-end"
          sx={{ height: "100%" }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0 }}>
            <Tooltip title="View Scorecard">
              <IconButton
                onClick={handleScoreCardClick}
                sx={{
                  height: isMobile ? "20px" : "24px",
                  color: "#FFFFFF",
                  marginRight: "2px",
                  marginTop: "1px",
                }}
              >
                {!loading && (
                  <PieChartIcon
                    style={{
                      height: isMobile ? "20px" : "24px",
                      color: "#FFFFFF",
                      marginTop: "1px",
                      marginRight: "2px",
                    }}
                  />
                )}
                {loading && <CircularProgress size={isMobile ? 12 : 15} />}
              </IconButton>
            </Tooltip>
          </Stack>
          {candidate?.willingToRelocate && (
            <Tooltip title="Relocation Ready">
              <TravelIcon
                fontSize={isMobile ? "small" : "medium"}
                sx={{
                  marginRight: "10px",
                  marginTop: "10px",
                  // color: "#FFFFFF !important",
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="View Profile">
            <Typography
              onClick={handleCandidateNameClick}
              sx={{
                cursor: "pointer",
                color: "#FFFFFF",
                fontSize: isMobile ? "12px" : "14px",
                whiteSpace: "nowrap",
                marginTop: "auto",
                mt: 2,
              }}
            >
              View Profile
            </Typography>
          </Tooltip>
        </Grid>
      </Grid>
    </Box>
  );
};

const CandidateMatches: React.FC<{
  onCloseMatch: any;
}> = ({ onCloseMatch }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const playgroundContext = usePlaygroundContext();
  if (!playgroundContext) {
    throw new Error("PlaygroundContext is not available");
  }
  const { playground, setPlayground } = playgroundContext;
  const companyName = playground.companyName;
  const candidates = playground.candidates as CandidateData[];
  const generatedResponse = playground.jobDescription;
  const responseTitle = playground.jobTitle ?? "";
  const [selectedCandidate, setSelectedCandidate] = useState<string | null>(
    candidates.length > 0 ? candidates[0].eximiusId : null // Set the first candidate as default
  );
  const [loadingCandidate, setLoadingCandidate] = useState(false);
  const [selectedCandidateDetail, setSelectedCandidateDetail] = useState<{
    eximiusId: string;
    jobId: string;
  } | null>(null);
  const [candidateDetailsOpen, setCandidateDetailsOpen] = useState(false);
  const [candidateMatchDataOpen, setCandidateMatchDataOpen] = useState(false);
  const [selectedCandidateDetails, setSelectedCandidateDetails] =
    useState<CandidateData>({
      eximiusId: "",
      firstName: "",
      lastName: "",
      yearsOfExperience: 0,
      location: {
        city: "",
        country: "",
        countryCode: "",
        latitude: 0,
        longitude: 0,
        postalCode: null,
        state: "",
        street: "", // Changed null to an empty string to match type string
      },
      difference_radius: null,
      eximScore: 0,
      willingToRelocate: false,
      source: "",
      jobTitle: "",
      search_results: {} as CandidateSearchResults, // Assuming you have a default structure for CandidateSearchResults
      annualRate: null,
      email: "",
      hotListed: "",
      hourlyRate: 0,
      id: "",
      lastUpdated: "",
      phone: [],
      status: "",
      city: "",
      state: "",
      education: [],
      certifications: [],
      experience: 0,
      workAuthorization: {} as WorkAuthorization, // Assuming a default value
      jobDescription: null,
      generateJd: {},
      scorecard: "",
    });
  const topRef = useRef<HTMLElement | null>(null); // Ref for the top of the component

  const scrollToTop = () => {
    window.scroll(0, 0);
  };

  // Trigger scrollToTop when component is loaded
  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if (candidates.length > 0) {
      const sortedCandidates = [...candidates].sort(
        (a, b) => (b.eximScore || 0) - (a.eximScore || 0)
      );
      const firstCandidate = sortedCandidates[0];
      setPlayground((prev) => ({ ...prev, selectedCandidate: firstCandidate }));

      setSelectedCandidate(firstCandidate.eximiusId);
      setSelectedCandidateDetails(firstCandidate); // Set the first candidate's details
    }
  }, [candidates]);

  const sortedCandidates = [...candidates].sort(
    (a, b) => (b.eximScore || 0) - (a.eximScore || 0)
  );

  const [scoreCardData, setScoreCardData] = useState<any>(null);
  const [openScoreCard, setOpenScoreCard] = useState(false); // State to control the dialog visibility
  const [loadingPopupOpen, setLoadingPopupOpen] = useState(false);
  const [loadingMap, setLoadingMap] = useState<{ [key: string]: boolean }>({});

  const { mutate: searchScoreCard } = useMutation({
    mutationFn: (payload: any) => getMockScoreCard(payload),
    onMutate: (variables: any) => {
      setLoadingMap((prev) => ({ ...prev, [variables.eximiusId]: true }));
      setLoadingPopupOpen(true);
    },
    onSuccess: (scoreCardData: any, variables: any) => {
      setScoreCardData(scoreCardData[0]);

      setOpenScoreCard(true);
      setLoadingPopupOpen(false);
    },
    onSettled: (data, error, variables: any) => {
      setLoadingMap((prev) => ({ ...prev, [variables.eximiusId]: false }));
      setLoadingPopupOpen(false);
    },
    onError: (error: any, variables: any) => {
      console.error("Error sending payload:", error);
      setLoadingPopupOpen(false);
    },
  });
  const { mutate: handleNameClick } = useMutation({
    mutationFn: (payload: any) => getMockCandidateData(payload),
    onSuccess: (candidateData: any) => {
      setLoadingCandidate(false);
      handleCandidateDrawer(candidateData[0]);
    },
    onError: (error: any) => {
      console.error("Error sending payload:", error);
    },
  });
  const [showjobScreeing, setShowjobScreeing] = useState(false);
  const selectedCandidateName = selectedCandidate
    ? candidates.find((candidate) => candidate?.eximiusId === selectedCandidate)
        ?.firstName
    : "";
  const handleDownload = (eximiusId: string, source: string) => {
    const link = document.createElement("a");
    link.href = source;
    link.download = `${eximiusId}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleScorecardDownload = (eximiusId: string, scorecard: string) => {
    const link = document.createElement("a");
    link.href = scorecard;
    link.download = `${eximiusId}`; // Customize download filename if needed
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleProceed = () => {
    setPlayground((prev) => ({ ...prev, page: 6 }));
    setShowjobScreeing(true);
  };

  const handleCloseJobScreening = () => {
    setShowjobScreeing(false);
  };
  const [isOpen, setIsOpen] = useState(false);

  const toggleDrawer = (open: boolean) => () => {
    setIsOpen(open);
  };
  const loadingCandidateComponent = () => {
    return loadingCandidate ? (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress sx={{ color: "#3a4572" }} />
      </Box>
    ) : (
      selectedCandidateDetail && (
        <CandidateDetail
          eximiusId={selectedCandidateDetail.eximiusId}
          onClose={handleCandidateDetailsClose}
          candidatesData={selectedCandidateDetails}
        />
      )
    );
  };
  const handleCandidateDetailsClose = () => {
    setCandidateDetailsOpen(false);
  };
  const handleScoreCardClose = () => {
    setOpenScoreCard(false);
  };

  const handleCandidateDrawer = (candidateData: CandidateData) => {
    if (!candidateData) return;

    setSelectedCandidateDetail({
      eximiusId: candidateData.eximiusId,
      jobId: "1234",
    });

    setSelectedCandidateDetails(candidateData);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column-reverse",
        alignItems: "center",
        gap: isMobile ? 1 : 2,
        p: isMobile ? 1 : 3,
        minHeight: isMobile ? "0" : "100vh",
      }}
      mt={{ xs: 0, sm: -2, md: -4 }}
      ref={topRef}
    >
      {/* Left Section - Candidate Matching */}
      <Card
        sx={{
          overflow: "visible",
          mt: { xs: 0, sm: -1, md: -2 },
          flex: 2,
          borderRadius: 2,
          backgroundColor: "transparent",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          position: "relative",
          width: "100%",
          maxWidth: isMobile ? "100%" : isTablet ? "90%" : "800px",
          height: isMobile ? "auto" : "400px",
          boxShadow: "none",
          px: { xs: "2%", sm: "5%", md: "8%", lg: "10%" }, // Responsive horizontal padding
          py: { xs: "3%", sm: "5%", md: "8%", lg: "10%" }, // Responsive vertical padding
        }}
      >
        {/* Check if candidates array has data, then display common information once */}
        {sortedCandidates.length > 0 ? (
          <div style={{ width: "100%" }}>
            {/* Display jobTitle, city, state, and years of experience once for the group */}
            <Box
              mt={1}
              sx={{
                maxHeight: isMobile ? "400px" : "500px",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                scrollbarWidth: "none",
                backgroundColor: "transparent",
                width: "100%",
              }}
            >
              {/* Now, map over candidates and display individual MatchCards */}
              {sortedCandidates.map((candidate, index) => (
                <MatchCard
                  loading={loadingMap[candidate?.eximiusId] || false}
                  searchScoreCard={searchScoreCard}
                  key={candidate?.eximiusId}
                  name={`${candidate?.firstName || ""}${
                    candidate?.firstName && candidate?.lastName ? " " : ""
                  }${candidate?.lastName || ""}`}
                  experience={candidate?.yearsOfExperience}
                  location={candidate?.location}
                  distance={candidate?.difference_radius}
                  match={Math.round(candidate?.eximScore)}
                  handleDownload={() =>
                    handleDownload(candidate?.eximiusId, candidate?.source)
                  }
                  handleScorecardDownload={() =>
                    handleScorecardDownload(
                      candidate?.eximiusId,
                      candidate?.scorecard
                    )
                  }
                  showHomeIcon={candidate?.difference_radius < 50}
                  showTravelIcon={candidate?.willingToRelocate}
                  onClick={() => {
                    setPlayground((prev) => ({
                      ...prev,
                      selectedCandidate: candidate,
                    }));
                    setSelectedCandidate(candidate?.eximiusId);
                    setSelectedCandidateDetails(candidate);
                  }}
                  handleNameClick={handleNameClick}
                  candidate={candidate}
                  sx={{
                    border: "1px solid rgba(66, 73, 101, 1)",

                    backgroundColor:
                      selectedCandidate === candidate?.eximiusId
                        ? "#3a4572"
                        : "#7C7C7C",
                    "&:hover": {
                      backgroundColor: "#3a4572",
                    },
                    "&:active": {
                      backgroundColor: "#3a4572",
                    },
                    cursor: "pointer",
                    color: "#FFFFFF",
                    padding: isMobile ? "8px" : "10px",
                    borderRadius: "8px",
                    marginBottom: "10px",
                    transition: "background-color 0.3s ease",
                  }}
                  setCandidateDetailsOpen={setCandidateDetailsOpen}
                  setLoadingCandidate={setLoadingCandidate}
                />
              ))}
            </Box>
          </div>
        ) : (
          <Typography variant="body1" color="#FFFFFF">
            No candidates found.
          </Typography>
        )}
        <Stack
          direction={isMobile ? "column-reverse" : "row"} // Reverse order on mobile
          spacing={isMobile ? 1 : 2}
          mt={isMobile ? 2 : 3}
          ml={isMobile ? 0 : 1}
          mr={isMobile ? 0 : 25}
          width={isMobile ? "100%" : "auto"}
        >
          <Button
            variant="outlined"
            type="button"
            sx={{
              textTransform: "none",
              color: "white",
              height: isMobile ? "40px" : "44px",
              borderRadius: "45px",
              padding: isMobile ? "10px 16px" : "12px 20px",
              fontSize: isMobile ? "14px" : "16px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#7C7C7C",
              width: isMobile ? "100%" : "auto",
            }}
            onClick={onCloseMatch}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              height: isMobile ? "40px" : "44px",
              color: "#fff",
              bgcolor: "#007BFF",
              borderRadius: "45px",
              padding: isMobile ? "10px 16px" : "12px 20px",
              fontSize: isMobile ? "14px" : "16px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              "&:hover": {
                bgcolor: "#0052CC",
              },
              "&:disabled": {
                bgcolor: "#0052CC",
                color: "#fff",
              },
              width: isMobile ? "100%" : "auto",
            }}
            onClick={handleProceed}
            disabled={!(sortedCandidates.length > 0)}
          >
            Start Engagement
          </Button>
        </Stack>
      </Card>

      {/* Right Section - Candidate Details */}
      <Card
        sx={{
          flex: 1,
          mb: isMobile ? 2 : { sm: -13, md: -14, lg: -17 },
          borderRadius: 2,
          backgroundColor: "transparent",
          width: isMobile ? "95%" : "90%",
          boxShadow: "none",
          height: "auto",
          px: { xs: 1, sm: 2, md: 3, lg: 4 }, // Horizontal padding for different screen sizes
          py: { xs: 1, sm: 1, md: 1, lg: 1 },
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 0,
            fontSize: "24px",
            background: "linear-gradient(90deg, #538DFF, #A365F3)",
            WebkitBackgroundClip: "text",
            color: "transparent",
          }}
        >
          Candidate Matching
        </Typography>

        <Typography
          variant="body1"
          sx={{
            mb: 1,
            color: "white",
            // fontSize: { xs: "16px", sm: "13px", md: "14px", lg: "inherit" },
            lineHeight: { xs: 1.4, sm: 1.5, md: "inherit" },
          }}
        >
          Our patent-pending AI ranks candidates with precision, analyzing
          context, not just keywords. It evaluates qualifications, skills,
          availability, interest, experience, and location to find the best fit,
          with each candidate ranked by match percentage.
        </Typography>
        <Typography
          sx={{
            fontWeight: 600,
            mb: 0,
            fontSize: { xs: "12px", sm: "14px" },
            background: "linear-gradient(90deg, #538DFF, #A365F3)",
            WebkitBackgroundClip: "text",
            color: "transparent",
            display: "block", // Ensures the entire text is shown
            overflowWrap: "break-word", // Prevents text from getting cut off
          }}
        >
          Why a 100% Candidate Match is a Myth (and Why That’s a Good Thing)
        </Typography>

        <Typography
          sx={{
            color: "white",
            fontSize: { xs: "11px", sm: "12px" },
            pb: { xs: 2, md: 0 },
            lineHeight: 1.5,
            textAlign: "left",
            whiteSpace: "normal", // Ensures text wraps properly
            overflow: "visible", // Prevents hidden text
            display: "block", // Makes sure the entire content is visible
          }}
        >
          At Eximius AI, we empower hiring managers with AI-driven talent
          intelligence to find the best-fit candidates—fast. While our
          technology delivers high-confidence matches (85-95%), a 100% match
          isn’t realistic –{" "}
          <Link
            component="button"
            onClick={() => setCandidateMatchDataOpen(true)}
            sx={{
              color: "#5B89FE",
              textDecoration: "none",
              fontWeight: "bold",
              cursor: "pointer",
              display: "inline", // Ensures it stays within the line
              whiteSpace: "nowrap", // Prevents breaking into a new line
            }}
          >
            Here’s why
          </Link>
          .
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: "white",
            mt: 1,
            mb: { xs: "300px", md: 0 },
            // fontSize: { xs: "14px", sm: "15px", md: "16px", lg: "inherit" }
          }}
        >
          Select a candidate and initiate engagement!
        </Typography>
      </Card>
      <SwipeableDrawer
        anchor="top"
        open={candidateMatchDataOpen}
        onOpen={() => setCandidateMatchDataOpen(true)}
        onClose={() => setCandidateMatchDataOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "80vw", sm: "80vw", md: "45vw", lg: "45vw" },
            height: "auto",
            maxHeight: "80vh",
            top: 0,
            right: { xs: "10vw", sm: "10vw", md: "10em" },
            left: "auto",
            // marginRight:{xs:"5vw"},
            padding: 0,
            marginTop: { xs: "4em", sm: "6em" }, // Adjust margin for smaller screens
            backgroundColor: "#ffffff",
            color: "black",
            overflowY: "auto",
            borderRadius: { xs: 0, sm: 1 }, // No border-radius on mobile for fullscreen effect
          },
        }}
      >
        {/* Close Button at Top-Right */}
        <Box sx={{ position: "absolute", top: 10, right: 10 }}>
          <IconButton onClick={() => setCandidateMatchDataOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <CandidateData />
      </SwipeableDrawer>

      <SwipeableDrawer
        anchor="right"
        open={candidateDetailsOpen}
        onClose={handleCandidateDetailsClose}
        onOpen={() => setCandidateDetailsOpen(true)}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100vw", sm: "80vw", md: "65vw", lg: "50vw" },
            height: "100vh",
            position: "fixed",
            top: 0,
            right: 0,
            padding: 0,
            margin: 0,
            backgroundColor: "#ffffff",
            color: "black",
            overflowY: "auto",
          },
        }}
      >
        {loadingCandidateComponent()}
      </SwipeableDrawer>
      <SwipeableDrawer
        anchor="right"
        open={openScoreCard}
        onClose={() => setOpenScoreCard(false)}
        onOpen={() => setOpenScoreCard(true)}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100vw", sm: "80vw", md: "65vw", lg: "50vw" },
            height: "100vh",
            position: "fixed",
            top: 0,
            right: 0,
            padding: { xs: "12px", sm: "14px", md: "16px" },
            margin: 0,
            backgroundColor: "#ffffff",
            color: "black",
            overflowY: "auto",
          },
        }}
      >
        <MessagePopup
          open={loadingPopupOpen}
          message="Our AI engine is generating a comprehensive scorecard citing how well the candidate matches the job requirements. Please wait..."
        />
        {openScoreCard && (
          <ScoreCardDetail
            scoreData={scoreCardData}
            jobId={scoreCardData?.jobId || ""}
            score={scoreCardData?.score}
            onClose={handleScoreCardClose}
          />
        )}
      </SwipeableDrawer>
    </Box>
  );
};

export default CandidateMatches;
